// src/auth/dev-persona-login.ts
/**
 * DEPLOYED-DEV-ONLY persona sign-in: `?login=<persona>` (alias `?devlogin=`).
 *
 * Purpose: parallel automated test sessions (Samer's, 2026-09-30) each need to
 * sign in as a different clinical persona on dev-practitioner-v2 without anyone
 * typing a password on a non-local host. Loading
 * `https://dev-practitioner-v2.scrips.com/?login=nurse` signs in as that
 * persona through the normal `login()` path, then removes the parameter from
 * the address bar.
 *
 * Two gates, BOTH required, checked here and nowhere else:
 *   1. BUILD: `VITE_DEV_PERSONAS` is set. Only `.github/workflows/dev-web.yml`
 *      sets it (from the `DEV_PERSONAS_JSON` repo secret). No committed env
 *      file carries it, so every other build — prod, IHL, local — has no
 *      persona map at all and this module returns null.
 *   2. RUNTIME: the page is served from a host in `DEV_PERSONA_HOSTS`. A dev
 *      bundle copied to any other host ignores the parameter.
 *
 * The persona map is never in source. Its shape is
 *   {"<persona>": {"username": "<email>", "password": "<password>"}, ...}
 * Keys are matched case-insensitively. An unknown persona, a malformed map or
 * a missing gate all mean "do nothing": the normal login page is shown.
 *
 * SECURITY NOTE. Vite inlines `VITE_*` into the shipped bundle, so every
 * password in the map is readable by anyone who loads the dev site. Agreed for
 * the synthetic dev accounts by Andrew, 2026-09-30. Never add an account that
 * exists outside dev, and never add a real person's account.
 */

export interface DevPersonaCredentials {
  username: string;
  password: string;
}

export interface DevPersonaLogin extends DevPersonaCredentials {
  persona: string;
}

/**
 * `?login=<persona>` is the primary form, next to the existing `?login=manual`;
 * `?devlogin=<persona>` is kept as an alias. `manual` is reserved and never
 * treated as a persona, so this module leaves `?login=manual` in the URL for
 * its own handler.
 */
export const DEV_LOGIN_PARAMS = ['login', 'devlogin'] as const;
export const MANUAL_LOGIN_VALUE = 'manual';

/** Hosts where `?login=<persona>` is honoured. Deployed dev only. */
export const DEV_PERSONA_HOSTS: readonly string[] = ['dev-practitioner-v2.scrips.com'];

/** Parse the build-time persona map. Anything malformed yields an empty map. */
export function parseDevPersonas(raw: string | undefined): Record<string, DevPersonaCredentials> {
  // Null prototype so a persona named `constructor` or `__proto__` can never
  // resolve to something inherited.
  const out: Record<string, DevPersonaCredentials> = Object.create(null);
  if (!raw) return out;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return out;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return out;
  for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (!value || typeof value !== 'object') continue;
    const { username, password } = value as Record<string, unknown>;
    if (typeof username !== 'string' || typeof password !== 'string') continue;
    if (!username || !password) continue;
    const persona = key.trim().toLowerCase();
    if (persona === MANUAL_LOGIN_VALUE) continue;
    out[persona] = { username, password };
  }
  return out;
}

/**
 * Read `?login=<persona>` (or `?devlogin=`) from `loc`, strip it via `replaceUrl`, and return the
 * persona's credentials. Returns null (and leaves the URL alone) unless both
 * gates pass. Pure apart from `replaceUrl`, so it is testable without a real
 * browser location.
 */
export function consumeDevLoginParam(
  raw: string | undefined,
  loc: { hostname: string; href: string },
  replaceUrl: (url: string) => void,
): DevPersonaLogin | null {
  if (!raw) return null;
  if (!DEV_PERSONA_HOSTS.includes(loc.hostname)) return null;
  const url = new URL(loc.href);
  const normalise = (v: string | null) => (v ?? '').trim().toLowerCase();
  const param = DEV_LOGIN_PARAMS.find(
    (p) => url.searchParams.has(p) && normalise(url.searchParams.get(p)) !== MANUAL_LOGIN_VALUE,
  );
  if (!param) return null;
  const persona = normalise(url.searchParams.get(param));
  url.searchParams.delete(param);
  replaceUrl(url.pathname + url.search + url.hash);
  const creds = parseDevPersonas(raw)[persona];
  return creds ? { persona, ...creds } : null;
}

/**
 * `?login=manual`: end the stored session and show the login form (mirrors the
 * PM app). Needed once a persona session is stored, because nothing else on
 * deployed dev reaches the form without the in-app sign-out. Same two gates as
 * the persona sign-in, so on every other build or host the parameter is inert
 * and a link to `?login=manual` cannot end a real user's session.
 */
export function consumeManualLoginParam(
  raw: string | undefined,
  loc: { hostname: string; href: string },
  replaceUrl: (url: string) => void,
): boolean {
  if (!raw) return false;
  if (!DEV_PERSONA_HOSTS.includes(loc.hostname)) return false;
  const url = new URL(loc.href);
  const value = (url.searchParams.get('login') ?? '').trim().toLowerCase();
  if (value !== MANUAL_LOGIN_VALUE) return false;
  url.searchParams.delete('login');
  replaceUrl(url.pathname + url.search + url.hash);
  return true;
}

function readManualAtBoot(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return consumeManualLoginParam(import.meta.env.VITE_DEV_PERSONAS, window.location, (u) =>
      window.history.replaceState(window.history.state, '', u),
    );
  } catch {
    return false;
  }
}

function readAtBoot(): DevPersonaLogin | null {
  // Guarded whole: imported by the auth store, which also runs in node/jsdom
  // tests where `window` or `history` may be absent. Any failure = no persona.
  try {
    if (typeof window === 'undefined') return null;
    return consumeDevLoginParam(import.meta.env.VITE_DEV_PERSONAS, window.location, (u) =>
      window.history.replaceState(window.history.state, '', u),
    );
  } catch {
    return null;
  }
}

/**
 * The persona requested on this page load, or null. Evaluated once at module
 * load, before the router reads the URL, so the stripped parameter never
 * reaches a route or a return-to redirect.
 */
export const DEV_PERSONA_LOGIN: DevPersonaLogin | null = readAtBoot();

/** True when `?login=manual` was requested on this page load (dev host, dev build). */
export const DEV_MANUAL_LOGIN_REQUESTED: boolean = readManualAtBoot();
